import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card, Descriptions, Tag, Typography, Button, Spin, List, Space, Badge, Select,
  message, Form, Input, InputNumber, Switch, Popconfirm, Image, Divider, Tabs, Modal,
} from 'antd';
import {
  ArrowLeft, Bed, Bath, Maximize, Trees, Waves, Car, Save, Trash2,
  Upload as UploadIcon, X, Film, GripVertical, ImageIcon, ExternalLink,
  MapPin, FileImage, Copy, Link as LinkIcon,
} from 'lucide-react';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { propertiesApi, uploadsApi, inmovillaApi } from '../../services/api';

const { Title, Text } = Typography;

const typeColors: Record<string, string> = {
  VILLA: 'green', APARTMENT: 'blue', FINCA: 'orange',
  PENTHOUSE: 'purple', TOWNHOUSE: 'cyan', COMMERCIAL: 'red', LAND: 'gold',
  GARAGE: 'geekblue', OFFICE: 'magenta', STORAGE: 'volcano',
  BUILDING: 'lime', ROOM: 'processing', OTHER: 'default',
};
const statusColors: Record<string, string> = {
  AVAILABLE: 'green', RESERVED: 'orange', SOLD: 'red', RENTED: 'blue', OFF_MARKET: 'default',
};

/* ─── Media item type ──────────────────────────── */
interface MediaItem {
  url: string;
  alt: string;
  order: number;
  type: 'image' | 'video';
  homeStaging?: boolean;
}

/* ─── Sortable thumbnail component ─────────────── */
function SortableMediaItem({
  item, index, onRemove, onToggleHomeStaging,
}: { item: MediaItem; index: number; onRemove: (idx: number) => void; onToggleHomeStaging?: (idx: number, val: boolean) => void }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `media-${index}`,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    position: 'relative',
    overflow: 'hidden',
    background: '#000',
  };

  return (
    <div style={{ display: 'inline-flex', flexDirection: 'column', borderRadius: 8, overflow: 'hidden', border: isDragging ? '2px solid #1677ff' : '2px solid transparent' }}>
      <div ref={setNodeRef} style={style}>
        <div
          {...attributes}
          {...listeners}
          style={{
            position: 'absolute', top: 4, left: 4, zIndex: 10, cursor: 'grab',
            background: 'rgba(0,0,0,0.55)', borderRadius: 4, padding: '2px 4px',
            display: 'flex', alignItems: 'center',
          }}
        >
          <GripVertical size={14} color="#fff" />
        </div>

        <div
          style={{
            position: 'absolute', bottom: 4, left: 4, zIndex: 10,
            background: item.type === 'video' ? 'rgba(255,77,79,0.85)' : 'rgba(22,119,255,0.85)',
            borderRadius: 4, padding: '1px 6px', fontSize: 10, color: '#fff',
            display: 'flex', alignItems: 'center', gap: 3,
          }}
        >
          {item.type === 'video' ? <Film size={10} /> : <ImageIcon size={10} />}
          {item.type === 'video' ? 'Vídeo' : 'Imagen'}
        </div>

        <Button
          type="text" danger size="small" icon={<X size={12} />}
          style={{
            position: 'absolute', top: 4, right: 4, zIndex: 10,
            background: 'rgba(255,255,255,0.85)', borderRadius: '50%',
            width: 22, height: 22, padding: 0,
          }}
          onClick={(e) => { e.stopPropagation(); onRemove(index); }}
        />

        {item.type === 'video' ? (
          <video src={item.url} style={{ width: 160, height: 120, objectFit: 'cover', display: 'block' }} muted />
        ) : (
          <Image
            src={item.url}
            alt={item.alt || `Media ${index + 1}`}
            width={160}
            height={120}
            style={{ objectFit: 'cover', display: 'block' }}
            preview={{ mask: 'Vista previa' }}
            fallback="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYwIiBoZWlnaHQ9IjEyMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjBmMGYwIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtc2l6ZT0iMTQiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIiBmaWxsPSIjOTk5Ij5TaW4gaW1hZ2VuPC90ZXh0Pjwvc3ZnPg=="
          />
        )}
      </div>

      {/* Home Staging toggle — outside sortable ref so clicks work */}
      {item.type === 'image' && onToggleHomeStaging && (
        <div
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            padding: '5px 0', background: '#fafafa', cursor: 'default',
          }}
        >
          <Switch
            size="small"
            checked={!!item.homeStaging}
            onChange={(v) => onToggleHomeStaging(index, v)}
          />
          <span style={{ fontSize: 11, color: item.homeStaging ? '#cfb56b' : '#999', fontWeight: item.homeStaging ? 600 : 400 }}>Home Staging</span>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   Main component — Full property edit page
   ═══════════════════════════════════════════════════ */
export default function PropertyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [property, setProperty] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingFloorPlan, setUploadingFloorPlan] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [inmSyncModalOpen, setInmSyncModalOpen] = useState(false);
  const [idealistaSyncModalOpen, setIdealistaSyncModalOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const floorPlanInputRef = useRef<HTMLInputElement>(null);
  const [form] = Form.useForm();
  const navigate = useNavigate();

  /* ── Media state ─── */
  const getMediaItems = useCallback((prop: any): MediaItem[] => {
    const items: MediaItem[] = [];
    if (Array.isArray(prop?.images)) {
      prop.images.forEach((img: any) => {
        items.push({
          url: img.url, alt: img.alt || '',
          order: img.order ?? items.length,
          type: img.type || 'image',
          homeStaging: img.homeStaging || false,
        });
      });
    }
    if (prop?.videoUrl && !items.some((m) => m.url === prop.videoUrl)) {
      items.push({ url: prop.videoUrl, alt: 'Vídeo', order: items.length, type: 'video' });
    }
    items.sort((a, b) => a.order - b.order);
    return items;
  }, []);

  const [media, setMedia] = useState<MediaItem[]>([]);

  const fetchProperty = () => {
    if (!id) return;
    setLoading(true);
    propertiesApi.get(id)
      .then((r) => {
        const p = r.data;
        setProperty(p);
        setMedia(getMediaItems(p));
        form.setFieldsValue({
          title: p.title,
          type: p.type,
          status: p.status,
          price: p.price,
          currency: p.currency || 'EUR',
          priceType: p.priceType || 'sale',
          city: p.city,
          zone: p.zone,
          neighborhood: p.neighborhood,
          address: p.address,
          postalCode: p.postalCode,
          country: p.country,
          bedrooms: p.bedrooms,
          bathrooms: p.bathrooms,
          size: p.size,
          plotSize: p.plotSize,
          yearBuilt: p.yearBuilt,
          floors: p.floors,
          energyRating: p.energyRating,
          description: p.description,
          pool: p.pool,
          garden: p.garden,
          terrace: p.terrace,
          seaView: p.seaView,
          garage: p.garage,
          airConditioning: p.airConditioning,
          heating: p.heating,
          elevator: p.elevator,
          reference: p.reference,
          featured: p.featured,
          published: p.published,
          publishInmovilla: p.publishInmovilla,
          inmSendAddress: p.inmSendAddress,
          publishIdealista: p.publishIdealista,
          idealistaSendAddress: p.idealistaSendAddress,
          badgeNuevo: p.badgeNuevo || false,
          badgeOportunidad: p.badgeOportunidad || false,
          virtualTourUrl: p.virtualTourUrl,
          floorPlanUrl: p.floorPlanUrl,
          latitude: p.latitude,
          longitude: p.longitude,
          tags: p.tags,
          metaTitle: p.metaTitle,
          metaDescription: p.metaDescription,
        });
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchProperty(); }, [id]);

  /* ── Save all fields ─── */
  const handleSave = async (values: any) => {
    if (!id) return;
    setSaving(true);
    try {
      const { data } = await propertiesApi.update(id, values);
      setProperty({ ...property, ...data });
      if (data?.idealistaSyncStatus === 'ERROR') {
        message.warning(`Guardada, pero Idealista falló: ${data.idealistaSyncError || 'error desconocido'}`);
      } else if (values.publishIdealista && data?.idealistaSyncStatus === 'SYNCED') {
        message.success(`Propiedad guardada y sincronizada con Idealista · ID ${data.idealistaId}`);
      } else {
        message.success('Propiedad guardada');
      }
    } catch {
      message.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    try {
      await propertiesApi.delete(id);
      message.success('Propiedad eliminada');
      navigate('/properties');
    } catch { message.error('Error al eliminar'); }
  };

  /* ── Persist media to backend ─── */
  const persistMedia = async (items: MediaItem[]) => {
    if (!id) return;
    const normalized = items.map((m, i) => ({ url: m.url, alt: m.alt, order: i, type: m.type, homeStaging: m.homeStaging || false }));
    const videoItem = normalized.find((m) => m.type === 'video');
    try {
      await propertiesApi.update(id, { images: normalized, videoUrl: videoItem?.url || null });
    } catch {
      message.error('Error al guardar medios');
    }
  };

  /* ── Upload ─── */
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !id) return;
    setUploading(true);

    const imageFiles = files.filter((f) => f.type.startsWith('image/'));
    const videoFiles = files.filter((f) => f.type.startsWith('video/'));

    try {
      const newItems: MediaItem[] = [...media];

      if (imageFiles.length > 0) {
        const res = await uploadsApi.uploadImages(id, imageFiles);
        (res.data.files || []).forEach((f: any) => {
          newItems.push({ url: f.url, alt: f.originalName || '', order: newItems.length, type: 'image' });
        });
      }
      if (videoFiles.length > 0) {
        const res = await uploadsApi.uploadVideos(id, videoFiles);
        (res.data.files || []).forEach((f: any) => {
          newItems.push({ url: f.url, alt: f.originalName || '', order: newItems.length, type: 'video' });
        });
      }

      const reindexed = newItems.map((m, i) => ({ ...m, order: i }));
      setMedia(reindexed);
      await persistMedia(reindexed);
      message.success(`${files.length} archivo(s) subido(s)`);
    } catch {
      message.error('Error al subir archivos');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  /* ── Remove ─── */
  const handleRemoveMedia = async (idx: number) => {
    const removed = media[idx];
    const updated = media.filter((_, i) => i !== idx).map((m, i) => ({ ...m, order: i }));
    setMedia(updated);
    await persistMedia(updated);
    if (removed?.url) {
      const parts = removed.url.split('/');
      const filename = parts[parts.length - 1];
      const folder = removed.type === 'video' ? `properties/${id}/videos` : `properties/${id}`;
      uploadsApi.deleteFile(filename, folder).catch(() => {});
    }
    message.success(removed.type === 'video' ? 'Vídeo eliminado' : 'Imagen eliminada');
  };

  /* ── Drag & drop ─── */
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  /* ── Floor plan upload ─── */
  const handleFloorPlanUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !id) return;
    setUploadingFloorPlan(true);
    try {
      const res = await uploadsApi.uploadImages(id, files);
      const uploaded = res.data.files?.[0];
      if (uploaded?.url) {
        form.setFieldValue('floorPlanUrl', uploaded.url);
        await propertiesApi.update(id, { floorPlanUrl: uploaded.url });
        setProperty((prev: any) => ({ ...prev, floorPlanUrl: uploaded.url }));
        message.success('Plano subido correctamente');
      }
    } catch {
      message.error('Error al subir el plano');
    } finally {
      setUploadingFloorPlan(false);
      if (floorPlanInputRef.current) floorPlanInputRef.current.value = '';
    }
  };

  const handleRemoveFloorPlan = async () => {
    if (!id) return;
    form.setFieldValue('floorPlanUrl', null);
    await propertiesApi.update(id, { floorPlanUrl: null });
    setProperty((prev: any) => ({ ...prev, floorPlanUrl: null }));
    message.success('Plano eliminado');
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = media.findIndex((_, i) => `media-${i}` === active.id);
    const newIdx = media.findIndex((_, i) => `media-${i}` === over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    const reordered = arrayMove(media, oldIdx, newIdx).map((m, i) => ({ ...m, order: i }));
    setMedia(reordered);
    await persistMedia(reordered);
    message.success('Orden actualizado');
  };

  const handleToggleHomeStaging = async (index: number, checked: boolean) => {
    const updated = media.map((m, i) => (i === index ? { ...m, homeStaging: checked } : m));
    setMedia(updated);
    await persistMedia(updated);
  };

  /* ─── Render ─── */
  if (loading) return <div style={{ textAlign: 'center', padding: 100 }}><Spin size="large" /></div>;
  if (!property) return <Text type="danger">Propiedad no encontrada</Text>;

  const imageCount = media.filter((m) => m.type === 'image').length;
  const videoCount = media.filter((m) => m.type === 'video').length;

  return (
    <div>
      {/* ─── Header ────── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <Space>
          <Button type="text" icon={<ArrowLeft size={16} />} onClick={() => navigate('/properties')}>Volver</Button>
          <Title level={3} style={{ margin: 0 }}>{property.title}</Title>
          <Tag color={typeColors[property.type]}>{property.type}</Tag>
          <Tag color={statusColors[property.status]}>{property.status}</Tag>
          {property.reference && <Text type="secondary">Ref: {property.reference}</Text>}
        </Space>
        <Space>
          {property.slug && (() => {
            const webBase = (import.meta as any).env?.VITE_PUBLIC_WEB_URL || 'https://www.swomey.com';
            const publicUrl = `${webBase}/propiedades/${property.slug}`;
            return (
              <Space.Compact>
                <Button
                  icon={<Copy size={14} />}
                  onClick={() => {
                    navigator.clipboard.writeText(publicUrl);
                    message.success('URL copiada al portapapeles');
                  }}
                  title={publicUrl}
                >
                  Copiar URL pública
                </Button>
                <Button
                  icon={<LinkIcon size={14} />}
                  onClick={() => window.open(publicUrl, '_blank', 'noopener')}
                  title="Abrir en nueva pestaña"
                />
              </Space.Compact>
            );
          })()}
          <Button
            type="primary"
            icon={<Save size={14} />}
            loading={saving}
            onClick={() => form.submit()}
          >
            Guardar cambios
          </Button>
          <Popconfirm title="¿Eliminar esta propiedad?" description="Esta acción no se puede deshacer" onConfirm={handleDelete} okText="Eliminar" cancelText="Cancelar" okButtonProps={{ danger: true }}>
            <Button danger icon={<Trash2 size={14} />}>Eliminar</Button>
          </Popconfirm>
        </Space>
      </div>

      <Form form={form} layout="vertical" onFinish={handleSave}>
        <Tabs
          defaultActiveKey="general"
          type="card"
          items={[
            /* ═══ Tab 1: General ═══════════════════════════ */
            {
              key: 'general',
              label: 'Información general',
              children: (
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16 }}>
                  <Card size="small" title="Datos principales">
                    <Form.Item name="title" label="Título" rules={[{ required: true, message: 'Obligatorio' }]}>
                      <Input placeholder="Villa Sunset en primera línea de playa" />
                    </Form.Item>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                      <Form.Item name="type" label="Tipo">
                        <Select options={Object.keys(typeColors).map((k) => ({ value: k, label: k }))} />
                      </Form.Item>
                      <Form.Item name="status" label="Estado">
                        <Select options={Object.keys(statusColors).map((k) => ({ value: k, label: k }))} />
                      </Form.Item>
                      <Form.Item name="reference" label="Referencia">
                        <Input placeholder="SWM-001" />
                      </Form.Item>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                      <Form.Item name="price" label="Precio (€)" rules={[{ required: true, message: 'Obligatorio' }]}>
                        <InputNumber style={{ width: '100%' }} min={0} step={1000} formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')} parser={(v) => (Number(v?.replace(/\./g, '')) || 0) as any} />
                      </Form.Item>
                      <Form.Item name="currency" label="Moneda">
                        <Select options={[{ value: 'EUR', label: '€ EUR' }, { value: 'USD', label: '$ USD' }, { value: 'GBP', label: '£ GBP' }]} />
                      </Form.Item>
                      <Form.Item name="priceType" label="Tipo de precio">
                        <Select options={[{ value: 'sale', label: 'Venta' }, { value: 'rent', label: 'Alquiler' }, { value: 'both', label: 'Ambos' }]} />
                      </Form.Item>
                    </div>

                    <Form.Item name="description" label="Descripción">
                      <Input.TextArea rows={4} placeholder="Descripción detallada de la propiedad..." />
                    </Form.Item>
                  </Card>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <Card size="small" title="Publicación">
                      <Form.Item name="featured" label="Destacada" valuePropName="checked">
                        <Switch checkedChildren="Sí" unCheckedChildren="No" />
                      </Form.Item>
                      <Form.Item name="published" label="Publicada en web" valuePropName="checked">
                        <Switch checkedChildren="Sí" unCheckedChildren="No" />
                      </Form.Item>
                      <Divider style={{ margin: '12px 0' }} />
                      <Form.Item name="badgeNuevo" label="Badge: Nuevo" valuePropName="checked"
                        extra={<span style={{ fontSize: 12, color: '#888' }}>Muestra la etiqueta “Nuevo” en la tarjeta de la propiedad</span>}
                      >
                        <Switch checkedChildren="Sí" unCheckedChildren="No" />
                      </Form.Item>
                      <Form.Item name="badgeOportunidad" label="Badge: Oportunidad" valuePropName="checked"
                        extra={<span style={{ fontSize: 12, color: '#888' }}>Muestra la etiqueta “Oportunidad” en la tarjeta de la propiedad</span>}
                      >
                        <Switch checkedChildren="Sí" unCheckedChildren="No" />
                      </Form.Item>
                      <Divider style={{ margin: '12px 0' }} />
                      <Form.Item name="publishInmovilla" label="Sincronizar con Inmovilla" valuePropName="checked"
                        extra={property.inmSyncStatus === 'ERROR' ? (
                          <Text type="danger" style={{ fontSize: 12 }}>Error: {property.inmSyncError}</Text>
                        ) : property.inmSyncStatus === 'SYNCED' ? (
                          <Text type="success" style={{ fontSize: 12 }}>Sincronizada · ID: {property.inmovillaId}</Text>
                        ) : null}
                      >
                        <Switch
                          checkedChildren="Sí" unCheckedChildren="No" loading={syncing}
                          onChange={(checked) => {
                            if (checked && !property.publishInmovilla) {
                              // Al activar: mostrar modal para preguntar sobre la dirección
                              form.setFieldValue('publishInmovilla', false); // revertir hasta confirmar
                              setInmSyncModalOpen(true);
                            } else if (!checked && property.publishInmovilla) {
                              // Al desactivar: guardar inmediatamente para desincronizar
                              form.setFieldValue('publishInmovilla', false);
                              setTimeout(() => form.submit(), 0);
                            }
                          }}
                        />
                      </Form.Item>

                      {/* Modal de confirmación de sincronización */}
                      <Modal
                        title="Sincronizar con Inmovilla"
                        open={inmSyncModalOpen}
                        onCancel={() => {
                          setInmSyncModalOpen(false);
                          form.setFieldValue('publishInmovilla', false);
                        }}
                        footer={[
                          <Button key="cancel" onClick={() => {
                            setInmSyncModalOpen(false);
                            form.setFieldValue('publishInmovilla', false);
                          }}>
                            Cancelar
                          </Button>,
                          <Button key="no-address" onClick={() => {
                            form.setFieldValue('publishInmovilla', true);
                            form.setFieldValue('inmSendAddress', false);
                            setInmSyncModalOpen(false);
                            // Auto-guardar con la configuración elegida
                            form.submit();
                          }}>
                            Sin dirección
                          </Button>,
                          <Button key="with-address" type="primary" onClick={() => {
                            form.setFieldValue('publishInmovilla', true);
                            form.setFieldValue('inmSendAddress', true);
                            setInmSyncModalOpen(false);
                            // Auto-guardar con la configuración elegida
                            form.submit();
                          }}>
                            Con dirección
                          </Button>,
                        ]}
                      >
                        <p>¿Deseas incluir la <strong>dirección exacta</strong> de la propiedad en la sincronización con Inmovilla?</p>
                        <p style={{ color: '#888', fontSize: 13 }}>
                          Si eliges <em>"Sin dirección"</em>, se enviarán todos los datos de la propiedad (fotos, características, precio, etc.) pero la dirección exacta y el código postal no se compartirán.
                        </p>
                      </Modal>
                      <Form.Item name="inmSendAddress" hidden><Input /></Form.Item>
                      <Divider style={{ margin: '12px 0' }} />
                      <Form.Item name="publishIdealista" label="Sincronizar con Idealista" valuePropName="checked"
                        extra={property.idealistaSyncStatus === 'ERROR' ? (
                          <Text type="danger" style={{ fontSize: 12 }}>Error: {property.idealistaSyncError}</Text>
                        ) : property.idealistaSyncStatus === 'SYNCED' ? (
                          <Text type="success" style={{ fontSize: 12 }}>Sincronizada · ID: {property.idealistaId}</Text>
                        ) : null}
                      >
                        <Switch
                          checkedChildren="Sí" unCheckedChildren="No" loading={saving}
                          onChange={(checked) => {
                            if (checked && !property.publishIdealista) {
                              form.setFieldValue('publishIdealista', false);
                              setIdealistaSyncModalOpen(true);
                            } else if (!checked && property.publishIdealista) {
                              form.setFieldValue('publishIdealista', false);
                              setTimeout(() => form.submit(), 0);
                            }
                          }}
                        />
                      </Form.Item>
                      <Modal
                        title="Sincronizar con Idealista"
                        open={idealistaSyncModalOpen}
                        onCancel={() => {
                          setIdealistaSyncModalOpen(false);
                          form.setFieldValue('publishIdealista', false);
                        }}
                        footer={[
                          <Button key="cancel" onClick={() => {
                            setIdealistaSyncModalOpen(false);
                            form.setFieldValue('publishIdealista', false);
                          }}>
                            Cancelar
                          </Button>,
                          <Button key="no-address" onClick={() => {
                            form.setFieldValue('publishIdealista', true);
                            form.setFieldValue('idealistaSendAddress', false);
                            setIdealistaSyncModalOpen(false);
                            form.submit();
                          }}>
                            Sin dirección
                          </Button>,
                          <Button key="with-address" type="primary" onClick={() => {
                            form.setFieldValue('publishIdealista', true);
                            form.setFieldValue('idealistaSendAddress', true);
                            setIdealistaSyncModalOpen(false);
                            form.submit();
                          }}>
                            Con dirección
                          </Button>,
                        ]}
                      >
                        <p>¿Deseas incluir la <strong>dirección exacta</strong> de la propiedad en la sincronización con Idealista?</p>
                        <p style={{ color: '#888', fontSize: 13 }}>
                          Si eliges <em>&quot;Sin dirección&quot;</em>, se enviarán fotos, características y precio. Idealista geolocalizará con coordenadas/CP si existen, pero sin calle y número.
                        </p>
                      </Modal>
                      <Form.Item name="idealistaSendAddress" hidden><Input /></Form.Item>
                    </Card>

                    <Card size="small" title="Oportunidades vinculadas">
                      {property.opportunities?.length > 0 ? (
                        <List size="small" dataSource={property.opportunities} renderItem={(opp: any) => (
                          <List.Item actions={[<Button type="link" size="small" icon={<ExternalLink size={12} />} onClick={() => navigate(`/opportunities/${opp.id}`)} />]}>
                            <List.Item.Meta
                              title={<Text style={{ fontSize: 13 }}>{opp.title || `${opp.lead?.firstName} ${opp.lead?.lastName || ''}`}</Text>}
                              description={<Tag style={{ fontSize: 11 }}>{opp.stage}</Tag>}
                            />
                          </List.Item>
                        )} />
                      ) : <Text type="secondary" style={{ fontSize: 13 }}>Sin oportunidades</Text>}
                    </Card>
                  </div>
                </div>
              ),
            },

            /* ═══ Tab 2: Ubicación ═════════════════════════ */
            {
              key: 'location',
              label: 'Ubicación',
              children: (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <Card size="small" title="Dirección">
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                      <Form.Item name="address" label="Dirección"><Input placeholder="Calle, número..." /></Form.Item>
                      <Form.Item name="city" label="Ciudad"><Input /></Form.Item>
                      <Form.Item name="zone" label="Zona / Urbanización"><Input /></Form.Item>
                      <Form.Item name="neighborhood" label="Barrio"><Input placeholder="Ej: Santa Catalina, Son Vida..." /></Form.Item>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                      <Form.Item name="postalCode" label="Código postal"><Input /></Form.Item>
                      <Form.Item name="country" label="País"><Input /></Form.Item>
                    </div>
                  </Card>

                  <Card size="small" title={<Space><MapPin size={16} />Coordenadas del mapa</Space>}
                    extra={
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Se usan para mostrar el mapa en la ficha pública
                      </Text>
                    }
                  >
                    <div style={{ marginBottom: 12 }}>
                      <label style={{ display: 'block', marginBottom: 4, fontSize: 13, fontWeight: 500 }}>
                        Pegar URL de Google Maps
                      </label>
                      <Input
                        placeholder="https://www.google.com/maps/place/.../@39.5696,2.6502,17z/..."
                        allowClear
                        onChange={(e) => {
                          const url = e.target.value;
                          if (!url) return;
                          // Match /@lat,lng  or ?q=lat,lng  or ?ll=lat,lng  or !3d lat !4d lng
                          let lat: number | null = null;
                          let lng: number | null = null;
                          const atMatch = url.match(/@(-?\d+\.?\d*),(-?\d+\.?\d*)/);
                          const qMatch = url.match(/[?&]q=(-?\d+\.?\d*),(-?\d+\.?\d*)/);
                          const llMatch = url.match(/[?&]ll=(-?\d+\.?\d*),(-?\d+\.?\d*)/);
                          const dMatch = url.match(/!3d(-?\d+\.?\d*)!4d(-?\d+\.?\d*)/);
                          const m = atMatch || qMatch || llMatch || dMatch;
                          if (m) {
                            lat = parseFloat(m[1]);
                            lng = parseFloat(m[2]);
                          }
                          if (lat && lng && !isNaN(lat) && !isNaN(lng)) {
                            form.setFieldsValue({ latitude: lat, longitude: lng });
                            message.success(`Coordenadas extraídas: ${lat}, ${lng}`);
                          }
                        }}
                        suffix={<MapPin size={14} style={{ color: '#999' }} />}
                      />
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        Busca la ubicación en Google Maps, copia la URL del navegador y pégala aquí
                      </Text>
                    </div>
                    <Divider style={{ margin: '8px 0 12px' }} />
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                      <Form.Item name="latitude" label="Latitud" extra="Ej: 39.5696">
                        <InputNumber style={{ width: '100%' }} step={0.0001} precision={6} placeholder="39.5696" />
                      </Form.Item>
                      <Form.Item name="longitude" label="Longitud" extra="Ej: 2.6502">
                        <InputNumber style={{ width: '100%' }} step={0.0001} precision={6} placeholder="2.6502" />
                      </Form.Item>
                    </div>
                    {form.getFieldValue('latitude') && form.getFieldValue('longitude') && (
                      <div style={{ marginTop: 8 }}>
                        <a
                          href={`https://www.google.com/maps?q=${form.getFieldValue('latitude')},${form.getFieldValue('longitude')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                        >
                          <ExternalLink size={13} /> Ver en Google Maps
                        </a>
                      </div>
                    )}
                  </Card>

                  <Card size="small" title={<Space><FileImage size={16} />Plano de la vivienda</Space>}
                    extra={
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Se muestra en la ficha pública encima de la ubicación
                      </Text>
                    }
                  >
                    <input ref={floorPlanInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFloorPlanUpload} />
                    <Form.Item name="floorPlanUrl" label="URL del plano" style={{ marginBottom: 8 }}>
                      <Input placeholder="https://..." readOnly />
                    </Form.Item>
                    {property?.floorPlanUrl ? (
                      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                        <Image
                          src={property.floorPlanUrl}
                          alt="Plano de la vivienda"
                          width={240}
                          style={{ borderRadius: 8, border: '1px solid #d9d9d9' }}
                          preview={{ mask: 'Vista previa' }}
                        />
                        <Space direction="vertical" size={8}>
                          <Button
                            icon={<UploadIcon size={14} />}
                            loading={uploadingFloorPlan}
                            onClick={() => floorPlanInputRef.current?.click()}
                          >
                            Cambiar plano
                          </Button>
                          <Popconfirm title="¿Eliminar el plano?" onConfirm={handleRemoveFloorPlan} okText="Eliminar" cancelText="Cancelar" okButtonProps={{ danger: true }}>
                            <Button danger icon={<X size={14} />}>Eliminar</Button>
                          </Popconfirm>
                        </Space>
                      </div>
                    ) : (
                      <div
                        style={{
                          border: '2px dashed #d9d9d9', borderRadius: 8, padding: '32px 16px',
                          textAlign: 'center', cursor: 'pointer', color: '#999',
                        }}
                        onClick={() => floorPlanInputRef.current?.click()}
                      >
                        {uploadingFloorPlan ? (
                          <Spin />
                        ) : (
                          <>
                            <FileImage size={32} style={{ marginBottom: 8, opacity: 0.4 }} />
                            <div style={{ fontSize: 14, marginBottom: 4 }}>Haz clic para subir el plano de la vivienda</div>
                            <Text type="secondary" style={{ fontSize: 12 }}>JPG, PNG o WebP — máx 10MB</Text>
                          </>
                        )}
                      </div>
                    )}
                  </Card>
                </div>
              ),
            },

            /* ═══ Tab 3: Características ═══════════════════ */
            {
              key: 'features',
              label: 'Características',
              children: (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <Card size="small" title="Superficie y estructura">
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                      <Form.Item name="bedrooms" label="Habitaciones"><InputNumber style={{ width: '100%' }} min={0} /></Form.Item>
                      <Form.Item name="bathrooms" label="Baños"><InputNumber style={{ width: '100%' }} min={0} /></Form.Item>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                      <Form.Item name="size" label="Superficie construida (m²)"><InputNumber style={{ width: '100%' }} min={0} /></Form.Item>
                      <Form.Item name="plotSize" label="Parcela (m²)"><InputNumber style={{ width: '100%' }} min={0} /></Form.Item>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                      <Form.Item name="yearBuilt" label="Año construcción"><InputNumber style={{ width: '100%' }} /></Form.Item>
                      <Form.Item name="floors" label="Plantas"><InputNumber style={{ width: '100%' }} min={0} /></Form.Item>
                    </div>
                    <Form.Item name="energyRating" label="Certificado energético">
                      <Select allowClear placeholder="Seleccionar" options={['A', 'B', 'C', 'D', 'E', 'F', 'G'].map((v) => ({ value: v, label: `Clase ${v}` }))} />
                    </Form.Item>
                  </Card>

                  <Card size="small" title="Equipamientos y extras">
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 24px' }}>
                      <Form.Item name="pool" valuePropName="checked" style={{ marginBottom: 8 }}><Switch checkedChildren="Piscina" unCheckedChildren="Piscina" /></Form.Item>
                      <Form.Item name="garden" valuePropName="checked" style={{ marginBottom: 8 }}><Switch checkedChildren="Jardín" unCheckedChildren="Jardín" /></Form.Item>
                      <Form.Item name="terrace" valuePropName="checked" style={{ marginBottom: 8 }}><Switch checkedChildren="Terraza" unCheckedChildren="Terraza" /></Form.Item>
                      <Form.Item name="garage" valuePropName="checked" style={{ marginBottom: 8 }}><Switch checkedChildren="Garaje" unCheckedChildren="Garaje" /></Form.Item>
                      <Form.Item name="seaView" valuePropName="checked" style={{ marginBottom: 8 }}><Switch checkedChildren="Vista mar" unCheckedChildren="Vista mar" /></Form.Item>
                      <Form.Item name="airConditioning" valuePropName="checked" style={{ marginBottom: 8 }}><Switch checkedChildren="Aire acond." unCheckedChildren="Aire acond." /></Form.Item>
                      <Form.Item name="heating" valuePropName="checked" style={{ marginBottom: 8 }}><Switch checkedChildren="Calefacción" unCheckedChildren="Calefacción" /></Form.Item>
                      <Form.Item name="elevator" valuePropName="checked" style={{ marginBottom: 8 }}><Switch checkedChildren="Ascensor" unCheckedChildren="Ascensor" /></Form.Item>
                    </div>
                  </Card>
                </div>
              ),
            },

            /* ═══ Tab 4: Medios ════════════════════════════ */
            {
              key: 'media',
              label: (
                <Space size={4}>
                  Medios
                  {(imageCount + videoCount) > 0 && (
                    <Tag style={{ marginLeft: 4 }}>{imageCount + videoCount}</Tag>
                  )}
                </Space>
              ),
              children: (
                <Card
                  size="small"
                  title={
                    <Space>
                      <span>Galería de medios</span>
                      {imageCount > 0 && <Tag color="blue">{imageCount} imagen{imageCount !== 1 ? 'es' : ''}</Tag>}
                      {videoCount > 0 && <Tag color="red">{videoCount} vídeo{videoCount !== 1 ? 's' : ''}</Tag>}
                    </Space>
                  }
                  extra={
                    <Button icon={<UploadIcon size={14} />} loading={uploading} onClick={() => fileInputRef.current?.click()}>
                      Subir archivos
                    </Button>
                  }
                >
                  <input ref={fileInputRef} type="file" multiple accept="image/*,video/*" style={{ display: 'none' }} onChange={handleFileUpload} />

                  {media.length > 0 ? (
                    <>
                      <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 12 }}>
                        Arrastra los elementos para cambiar el orden. El primero será la imagen principal.
                      </Text>
                      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                        <SortableContext items={media.map((_, i) => `media-${i}`)} strategy={rectSortingStrategy}>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                            <Image.PreviewGroup>
                              {media.map((item, idx) => (
                                <SortableMediaItem key={`media-${idx}`} item={item} index={idx} onRemove={handleRemoveMedia} onToggleHomeStaging={handleToggleHomeStaging} />
                              ))}
                            </Image.PreviewGroup>
                          </div>
                        </SortableContext>
                      </DndContext>
                    </>
                  ) : (
                    <div
                      style={{
                        border: '2px dashed #d9d9d9', borderRadius: 8, padding: '40px 16px',
                        textAlign: 'center', cursor: 'pointer', color: '#999',
                      }}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <UploadIcon size={36} style={{ marginBottom: 8, opacity: 0.4 }} />
                      <div style={{ fontSize: 15, marginBottom: 4 }}>Haz clic o arrastra archivos aquí</div>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Imágenes (JPG, PNG, WebP — máx 10MB) · Vídeos (MP4, WebM — máx 100MB)
                      </Text>
                    </div>
                  )}

                  <Divider style={{ margin: '16px 0' }} />
                  <Form.Item name="virtualTourUrl" label="URL Tour Virtual" style={{ marginBottom: 0 }}>
                    <Input placeholder="https://my.matterport.com/..." />
                  </Form.Item>
                </Card>
              ),
            },

            /* ═══ Tab 5: SEO ═══════════════════════════════ */
            {
              key: 'seo',
              label: 'SEO',
              children: (
                <Card size="small" title="Metadatos y etiquetas">
                  <Form.Item name="metaTitle" label="Meta título">
                    <Input placeholder="Título para buscadores" />
                  </Form.Item>
                  <Form.Item name="metaDescription" label="Meta descripción">
                    <Input.TextArea rows={2} placeholder="Descripción para buscadores" />
                  </Form.Item>
                  <Form.Item name="tags" label="Etiquetas" extra="Separadas por comas">
                    <Input placeholder="lujo, primera línea, inversión" />
                  </Form.Item>
                </Card>
              ),
            },
          ]}
        />

        {/* ─── Sticky save bar ────── */}
        <div style={{
          position: 'sticky', bottom: 0, background: '#fff', padding: '12px 0',
          borderTop: '1px solid #f0f0f0', marginTop: 16, display: 'flex',
          justifyContent: 'flex-end', gap: 8, zIndex: 10,
        }}>
          <Button onClick={() => navigate('/properties')}>Cancelar</Button>
          <Button type="primary" icon={<Save size={14} />} loading={saving} htmlType="submit">
            Guardar cambios
          </Button>
        </div>
      </Form>
    </div>
  );
}
